import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth-lib';
import { Button } from '../design-system';
import './NotFound.css';

// The page for a URL that does not exist.
//
// Until now there was no top-level catch-all at all: an unknown staff URL fell
// through every <Route> and rendered a BLANK PAGE — indistinguishable from a
// crash, a slow load, or a permission problem. "Nothing happened" is the worst
// possible answer, because it gives the person no way to tell whether they
// mistyped, whether their access was removed, or whether the app is broken.
//
// ⚠️ WHERE "HOME" IS DEPENDS ON WHO IS ASKING, and getting that wrong is a real
// leak. A client-portal user must never be offered the staff cockpit, and a
// signed-out visitor must not be told a dashboard exists. So the destination is
// resolved from the session rather than hard-coded — see homeFor().
//
// `scope` lets the portal render this without touching the staff AuthContext,
// which its tree does not even provide (PortalAuthProvider is a separate one).

const DESTINATIONS = {
  portal: { to: '/portal', label: 'Back to your dashboard' },
  staff: { to: '/org-admin', label: 'Back to the dashboard' },
  employee: { to: '/employee', label: 'Back to your work' },
  guest: { to: '/login', label: 'Go to sign in' },
};

// A staffer below Partner lands in the employee area, not the cockpit — sending
// them to /org-admin would bounce them straight back out through the nav gate.
const PARTNER_LEVEL = 4;

function NotFound({ scope = 'staff' }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // ⚠️ Hooks cannot be called conditionally, so useAuth runs even in the portal
  // scope — where the staff AuthProvider IS still an ancestor (it wraps the
  // whole app), so this is safe. The RESULT is ignored for the portal, because
  // a client's session is a different context entirely and a staff user object
  // must never decide what a client is shown.
  const { user, isAuthenticated } = useAuth();

  const homeFor = () => {
    if (scope === 'portal') return DESTINATIONS.portal;
    if (!isAuthenticated) return DESTINATIONS.guest;
    if (user?.isSuperAdmin) return { to: '/admin', label: 'Back to the console' };
    return (user?.role?.level ?? 0) >= PARTNER_LEVEL ? DESTINATIONS.staff : DESTINATIONS.employee;
  };

  const home = homeFor();

  return (
    <main className="nf" role="main">
      <div className="nf-inner">
        {/* Decorative: the number is the loudest thing on the page, but a
            screen reader should hear the sentence, not "four zero four". */}
        <p className="nf-code" aria-hidden="true">404</p>

        <h1 className="nf-title">This page doesn&apos;t exist</h1>

        <p className="nf-body">
          The link may be out of date, or the address mistyped. Nothing has gone wrong with your
          account or your data.
        </p>

        {/* The URL that failed, shown verbatim. Someone reporting "it says 404"
            can copy this instead of describing it, and it is usually enough for
            them to spot their own typo. */}
        <p className="nf-path">
          <span className="nf-path-label">You asked for</span>
          <code>{pathname}</code>
        </p>

        <div className="nf-actions">
          <Button variant="primary" onClick={() => navigate(home.to, { replace: true })}>
            {home.label}
          </Button>
          {/* -1, not a fixed route: whatever they were looking at a moment ago
              is the thing they actually want back. */}
          <Button variant="secondary" onClick={() => navigate(-1)}>
            Go back
          </Button>
        </div>

        {/* Deliberately NOT shown to a portal user: "ask your administrator" is
            the right advice inside a firm and meaningless to a client, whose
            route out is their account manager, not an IT admin. */}
        {scope !== 'portal' && isAuthenticated && (
          <p className="nf-hint">
            If you reached this from a link inside the app, tell an admin — a menu entry may be
            pointing somewhere that has moved.
          </p>
        )}

        {scope !== 'portal' && !isAuthenticated && (
          <p className="nf-hint">
            Already have an account? <Link to="/login">Sign in</Link>.
          </p>
        )}
      </div>
    </main>
  );
}

export default NotFound;
